import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, API_BASE } from '../lib/api';
import {
  PageTitle, Card, Section, Pill, Btn, Kpi, Table, Th, Td, fmt,
} from '../components/ui-kit';
import { ScrollText, Trash2, Download, Mail } from 'lucide-react';

interface Treatment {
  id: string;
  name: string;
  purpose: string;
  legal_basis: string;
  data_categories?: string[];
  retention_days?: number;
  recipients?: string[];
}

export default function RGPD() {
  const { getToken, principal } = useXyqarAuth();
  const [items, setItems] = React.useState<Treatment[] | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ treatments: Treatment[] }>('/api/v1/auditor/rgpd', t);
        setItems(j.treatments ?? []);
      } catch { setItems([]); }
    })();
  }, [getToken]);

  const requestExport = async () => {
    setBusy('export'); setMsg(null);
    try {
      const t = await getToken().catch(() => '');
      const payload = await apiGet('/api/v1/me/export', t);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `xyqar-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setMsg('Export generated and downloaded locally from the self-serve RGPD endpoint (Art. 20).');
    } catch (e: any) {
      setMsg(`Export failed (${e.message}). If the issue persists, contact dpo@xyqar.com with the timestamp of this request.`);
    } finally { setBusy(null); }
  };

  const requestErasure = async () => {
    if (!confirm('Confirm right-to-erasure? This pseudonymises your records and emits an audit event. Action is logged.')) return;
    setBusy('erasure'); setMsg(null);
    try {
      const t = await getToken().catch(() => '');
      const r = await fetch(`${API_BASE}/api/v1/me`, {
        method: 'DELETE',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg('Erasure processed — your account data was pseudonymised and the audit event was sealed in the chain (RGPD Art. 17).');
    } catch (e: any) {
      setMsg(`Erasure failed (${e.message}). If the issue persists, contact dpo@xyqar.com with the timestamp of this request.`);
    } finally { setBusy(null); }
  };

  return (
    <div>
      <PageTitle
        kicker="RGPD · Privacy"
        title="Records of processing"
        right={<Pill tone="live"><ScrollText className="h-3 w-3" /> Art. 30 register</Pill>}
      />

      <Section title="At a glance">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Treatments"     value={fmt(items?.length)} accent="blue" />
          <Kpi label="Data residency" value="EU" hint="France Central + West Europe" />
          <Kpi label="Encryption"     value="AES-256" hint="at rest · TLS 1.3 in transit" />
          <Kpi label="DPO contact"    value="dpo@xyqar.com" />
        </div>
      </Section>

      <Section title="Treatments register" hint="One row per processing purpose. Reviewed quarterly by the DPO.">
        {items === null && <Card>Loading…</Card>}
        {items && items.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Name</Th><Th>Purpose</Th><Th>Legal basis</Th><Th>Data</Th><Th className="text-right">Retention</Th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-white/5 hover:bg-white/5 align-top">
                  <Td>
                    <p className="text-white text-sm">{t.name}</p>
                    <p className="text-[10px] text-white/30 font-mono mt-0.5">{t.id}</p>
                  </Td>
                  <Td><span className="text-white/80 text-xs">{t.purpose}</span></Td>
                  <Td><Pill tone="mute">{t.legal_basis}</Pill></Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {(t.data_categories ?? []).slice(0, 5).map((c, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 font-mono">{c}</span>
                      ))}
                    </div>
                  </Td>
                  <Td className="text-right"><span className="text-white/60 font-mono text-xs">{t.retention_days ? `${t.retention_days} d` : '—'}</span></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="Your rights" hint={`Logged-in as ${principal?.email ?? 'guest'} — exercise your rights below`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <Download className="h-5 w-5 text-white/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Art. 20 · Portability</p>
            <p className="text-white text-sm mt-1 mb-4">Receive a machine-readable export of your data.</p>
            <Btn primary onClick={requestExport} disabled={busy === 'export'}>{busy === 'export' ? 'Sending…' : 'Request export'}</Btn>
          </Card>
          <Card>
            <Trash2 className="h-5 w-5 text-rose-400 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Art. 17 · Erasure</p>
            <p className="text-white text-sm mt-1 mb-4">Pseudonymise your records. Audit-sealed.</p>
            <Btn onClick={requestErasure} disabled={busy === 'erasure'}>{busy === 'erasure' ? 'Sending…' : 'Request erasure'}</Btn>
          </Card>
          <Card>
            <Mail className="h-5 w-5 text-white/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">DPO contact</p>
            <p className="text-white text-sm mt-1 mb-4">Any other request, complaint, or CNIL escalation.</p>
            <Btn as="a" href="mailto:dpo@xyqar.com">dpo@xyqar.com</Btn>
          </Card>
        </div>

        {msg && (
          <Card className="mt-4 border-amber-500/30 bg-amber-500/5">
            <p className="text-amber-200 text-xs font-mono">{msg}</p>
          </Card>
        )}
      </Section>

      <Section title="Sub-processors & DPA">
        <Card>
          <p className="text-white/80 text-sm leading-relaxed">
            The full sub-processors list (Xyqar Identity, AWS, Azure, Brevo, Pappers, etc.) and the
            Data Processing Agreement template are available at{' '}
            <a href={`${API_BASE}/legal/dpa`} className="text-white underline">/legal/dpa</a>.
            For grand-compte deals the DPA is co-signed before pilot kick-off.
          </p>
        </Card>
      </Section>
    </div>
  );
}
